
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Database, Upload, FileText, Copy } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useLLMAgent } from '@/hooks/useLLMAgent';

interface DatabaseSchemaMapperProps {
  userId: string;
}

const DatabaseSchemaMapper: React.FC<DatabaseSchemaMapperProps> = ({ userId }) => {
  const { toast } = useToast();
  const [databaseType, setDatabaseType] = useState<string>('postgresql');
  const [schemaQuery, setSchemaQuery] = useState<string>('');
  const [schemaDefinition, setSchemaDefinition] = useState<string>('');
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string>('');
  const { generateResponse, isLoading } = useLLMAgent({ apiKeyId: selectedApiKeyId });

  // Check if user has any API keys on component mount
  React.useEffect(() => {
    if (userId) {
      checkForApiKeys();
    }

    // Set default schema queries based on database type
    setDefaultSchemaQuery(databaseType);
  }, [userId, databaseType]);

  const checkForApiKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('id, provider')
        .eq('user_id', userId)
        .limit(1);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setSelectedApiKeyId(data[0].id);
      }
    } catch (error) {
      console.error('Error checking for API keys:', error);
    }
  };

  const setDefaultSchemaQuery = (dbType: string) => {
    switch (dbType) {
      case 'postgresql':
        setSchemaQuery(`SELECT 
  table_schema, 
  table_name, 
  column_name, 
  data_type, 
  is_nullable,
  column_default
FROM 
  information_schema.columns 
WHERE 
  table_schema NOT IN ('pg_catalog', 'information_schema')
ORDER BY 
  table_schema, 
  table_name, 
  ordinal_position;

-- For foreign keys/relationships
SELECT
  tc.table_schema, 
  tc.constraint_name, 
  tc.table_name, 
  kcu.column_name, 
  ccu.table_schema AS foreign_table_schema,
  ccu.table_name AS foreign_table_name,
  ccu.column_name AS foreign_column_name 
FROM 
  information_schema.table_constraints AS tc 
  JOIN information_schema.key_column_usage AS kcu
    ON tc.constraint_name = kcu.constraint_name
    AND tc.table_schema = kcu.table_schema
  JOIN information_schema.constraint_column_usage AS ccu
    ON ccu.constraint_name = tc.constraint_name
    AND ccu.table_schema = tc.table_schema
WHERE tc.constraint_type = 'FOREIGN KEY';`);
        break;
      case 'mysql':
        setSchemaQuery(`SELECT 
  TABLE_SCHEMA,
  TABLE_NAME, 
  COLUMN_NAME, 
  DATA_TYPE, 
  IS_NULLABLE,
  COLUMN_DEFAULT
FROM 
  INFORMATION_SCHEMA.COLUMNS
WHERE 
  TABLE_SCHEMA = DATABASE()
ORDER BY 
  TABLE_NAME, 
  ORDINAL_POSITION;

-- For foreign keys/relationships
SELECT
  TABLE_SCHEMA,
  CONSTRAINT_NAME,
  TABLE_NAME,
  COLUMN_NAME,
  REFERENCED_TABLE_SCHEMA,
  REFERENCED_TABLE_NAME,
  REFERENCED_COLUMN_NAME
FROM
  INFORMATION_SCHEMA.KEY_COLUMN_USAGE
WHERE
  REFERENCED_TABLE_SCHEMA IS NOT NULL
  AND TABLE_SCHEMA = DATABASE();`);
        break;
      case 'sqlserver':
        setSchemaQuery(`SELECT 
  SCHEMA_NAME(t.schema_id) AS table_schema,
  t.name AS table_name,
  c.name AS column_name,
  ty.name AS data_type,
  c.is_nullable,
  c.default_object_id
FROM 
  sys.tables t
  INNER JOIN sys.columns c ON t.object_id = c.object_id
  INNER JOIN sys.types ty ON c.user_type_id = ty.user_type_id
ORDER BY 
  table_schema, 
  table_name, 
  c.column_id;

-- For foreign keys/relationships
SELECT 
  SCHEMA_NAME(f.schema_id) AS foreign_key_schema,
  OBJECT_NAME(f.parent_object_id) AS table_name,
  COL_NAME(fc.parent_object_id, fc.parent_column_id) AS column_name,
  SCHEMA_NAME(o.schema_id) AS referenced_schema,
  OBJECT_NAME(f.referenced_object_id) AS referenced_table,
  COL_NAME(fc.referenced_object_id, fc.referenced_column_id) AS referenced_column
FROM 
  sys.foreign_keys AS f
  INNER JOIN sys.foreign_key_columns AS fc ON f.object_id = fc.constraint_object_id
  INNER JOIN sys.objects AS o ON o.object_id = f.referenced_object_id;`);
        break;
      case 'oracle':
        setSchemaQuery(`SELECT 
  OWNER AS table_schema, 
  TABLE_NAME, 
  COLUMN_NAME, 
  DATA_TYPE, 
  NULLABLE AS is_nullable,
  DATA_DEFAULT AS column_default
FROM 
  ALL_TAB_COLUMNS
WHERE 
  OWNER = USER
ORDER BY 
  TABLE_NAME, 
  COLUMN_ID;

-- For foreign keys/relationships
SELECT
  a.owner AS table_schema,
  a.constraint_name,
  a.table_name,
  a.column_name,
  c_pk.owner AS foreign_table_schema,
  c_pk.table_name AS foreign_table_name,
  c_pk.column_name AS foreign_column_name
FROM
  all_cons_columns a
  JOIN all_constraints c ON a.owner = c.owner AND a.constraint_name = c.constraint_name
  JOIN all_constraints c_pk ON c.r_owner = c_pk.owner AND c.r_constraint_name = c_pk.constraint_name
  JOIN all_cons_columns c_pk_col ON c_pk.owner = c_pk_col.owner AND c_pk.constraint_name = c_pk_col.constraint_name
WHERE
  c.constraint_type = 'R'
  AND a.owner = USER;`);
        break;
      default:
        setSchemaQuery('');
    }
  };

  const handleDatabaseTypeChange = (value: string) => {
    setDatabaseType(value);
    setDefaultSchemaQuery(value);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setUploadedDocuments(filesArray);
    }
  };

  const generateSchemaPrompt = async () => {
    if (!schemaDefinition) {
      toast({
        title: "Schema Definition Required",
        description: "Please paste your database schema definition",
        variant: "destructive"
      });
      return;
    }

    if (uploadedDocuments.length === 0) {
      toast({
        title: "Documents Required",
        description: "Please upload at least one document to analyze",
        variant: "destructive"
      });
      return;
    }

    if (!selectedApiKeyId) {
      toast({
        title: "API Key Required",
        description: "Please add an API key for AI processing",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Build the system prompt for the LLM
      const systemPrompt = `You are a data extraction expert specializing in Standard Operating Procedures (SOPs).
Your task is to analyze the provided SOP documents and extract data that matches the provided database schema.

DATABASE SCHEMA:
${schemaDefinition}

INSTRUCTIONS:
1. Analyze the uploaded document carefully
2. Extract all relevant data that should be stored according to the schema
3. For each table in the schema, identify required fields and extract values for them
4. Validate the data format, especially ensuring dates match the expected format
5. For any references between tables, ensure the relationship is maintained
6. Generate a structured JSON output that matches the schema structure
7. Return ONLY valid JSON that can be parsed and used to generate SQL INSERT statements

Your output should be a valid JSON that can be directly used to generate SQL INSERT statements.`;

      // Call the LLM agent
      const result = await generateResponse(systemPrompt, "Generate a data extraction prompt for SOPs based on the provided database schema.");
      
      if (result) {
        setGeneratedPrompt(result);
        toast({
          title: "Prompt Generated",
          description: "Your schema analysis and prompt have been generated successfully!"
        });
      }
    } catch (error) {
      console.error('Error generating prompt:', error);
      toast({
        title: "Error",
        description: "Failed to generate the prompt. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "The content has been copied to your clipboard"
    });
  };

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5 text-[#0EA5E9]" />
          Database Schema Mapper
        </CardTitle>
        <CardDescription>
          Map your database schema to extract structured data from your SOPs
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="databaseType">Database Type</Label>
          <Select 
            value={databaseType} 
            onValueChange={handleDatabaseTypeChange}
          >
            <SelectTrigger className="w-full border-[#0EA5E9]/30">
              <SelectValue placeholder="Select database type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="postgresql">PostgreSQL</SelectItem>
              <SelectItem value="mysql">MySQL</SelectItem>
              <SelectItem value="sqlserver">SQL Server</SelectItem>
              <SelectItem value="oracle">Oracle</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="schemaQuery">Schema Query</Label>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-xs text-[#0EA5E9]"
              onClick={() => copyToClipboard(schemaQuery)}
            >
              <Copy className="h-3 w-3 mr-1" /> Copy
            </Button>
          </div>
          <Textarea
            id="schemaQuery"
            placeholder="SQL query to extract schema information"
            className="h-32 border-[#0EA5E9]/30 font-mono text-sm"
            value={schemaQuery}
            onChange={(e) => setSchemaQuery(e.target.value)}
          />
          <p className="text-xs text-gray-500">
            Run this query in your database to extract schema information, then paste the results below.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="schemaDefinition">Database Schema Definition</Label>
          <Textarea
            id="schemaDefinition"
            placeholder="Paste your database schema information here"
            className="h-48 border-[#0EA5E9]/30 font-mono text-sm"
            value={schemaDefinition}
            onChange={(e) => setSchemaDefinition(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="documentUpload">Upload SOP Documents</Label>
          <div className="border-2 border-dashed border-[#0EA5E9]/30 rounded-lg p-4 flex flex-col items-center justify-center">
            <Upload className="h-8 w-8 text-[#0EA5E9]/50 mb-2" />
            <p className="text-sm text-gray-500 mb-2">
              Upload PDF documents containing your Standard Operating Procedures
            </p>
            <Input
              id="documentUpload"
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.txt"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              variant="outline"
              className="border-[#0EA5E9] text-[#0EA5E9] hover:bg-[#0EA5E9]/10"
              onClick={() => document.getElementById('documentUpload')?.click()}
            >
              <Upload className="h-4 w-4 mr-2" /> Select Files
            </Button>
          </div>
          {uploadedDocuments.length > 0 && (
            <div className="mt-2">
              <Label>Uploaded Documents:</Label>
              <ul className="mt-1 space-y-1">
                {uploadedDocuments.map((doc, index) => (
                  <li key={index} className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4 text-[#0EA5E9]" />
                    {doc.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <Button 
          onClick={generateSchemaPrompt}
          disabled={!schemaDefinition || uploadedDocuments.length === 0 || !selectedApiKeyId || isGenerating || isLoading}
          className="w-full bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90 mt-4"
        >
          {isGenerating || isLoading ? 'Generating...' : 'Generate Extraction Prompt'}
        </Button>

        {generatedPrompt && (
          <div className="space-y-2 mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
            <div className="flex justify-between items-center">
              <Label>Generated Prompt:</Label>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-xs text-[#0EA5E9]"
                onClick={() => copyToClipboard(generatedPrompt)}
              >
                <Copy className="h-3 w-3 mr-1" /> Copy
              </Button>
            </div>
            <pre className="whitespace-pre-wrap bg-gray-100 p-3 rounded text-sm overflow-auto max-h-[300px]">
              {generatedPrompt}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DatabaseSchemaMapper;
